import { useGetGeolocalizzazioneComuni } from '@framework/api-client/api/inquiries/geolocalizzazione-controller/geolocalizzazione-controller.ts';
import { useFormSectionContext } from '@framework/form/form/section/FormSectionContext.tsx';
import {
  type ISelectInputProps,
  SelectInput,
} from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mapProvinceList } from '../../province/utils/mapProvinceList.ts';

export interface IMapProvinceMunicipalitySelectInputProps
  extends ISelectInputProps {
  name: string;
  provinceName: string;
  municipalityGroupList?: string[];
}

/**
 * Componente che gestisce le select di provincia e comune.
 */
export function MapProvinceMunicipalitySelectInput(
  props: IMapProvinceMunicipalitySelectInputProps,
) {
  const {
    name: municipalityName,
    provinceName,
    municipalityGroupList,
    ...otherProps
  } = props;

  const { data, isFetching, isLoading } = useGetGeolocalizzazioneComuni(
    { listaCodiceGruppo: municipalityGroupList ?? [] },
    {
      query: {
        enabled: true,
      },
      request: {
        arrayFormat: 'repeat',
      },
    },
  );

  const { enabled } = useFormSectionContext();
  const selectedProvince = useWatch({ name: provinceName });

  const { t } = useTranslation();
  const { provinces, towns } = mapProvinceList(data);

  // Mostro solo i comuni della provincia selezionata
  const filteredTowns = useMemo(() => {
    if (!(selectedProvince && towns)) return [];
    return towns.filter(town => town.context?.provincia === selectedProvince);
  }, [selectedProvince, towns]);
  return (
    <RowElement>
      <FormItem
        name={provinceName}
        label={t(
          'mappa.controlli.cerca-particella.modale.form-label.provincia',
          'Provincia',
        )}
      >
        <SelectInput
          {...otherProps}
          name={provinceName}
          loading={isFetching || isLoading}
          searchable
          placeholder={t(
            'mappa.controlli.cerca-particella.modale.form-label.provincia',
            'Provincia',
          )}
          options={provinces}
          allowClear
        />
      </FormItem>
      <FormItem
        name={municipalityName}
        label={t(
          'mappa.controlli.cerca-particella.modale.form-label.comune',
          'Comune',
        )}
      >
        <SelectInput
          {...otherProps}
          name={municipalityName}
          placeholder={t(
            'mappa.controlli.cerca-particella.modale.form-label.comune',
            'Comune',
          )}
          loading={isFetching || isLoading}
          searchable
          disabled={!selectedProvince || enabled === false}
          options={filteredTowns}
          allowClear
        />
      </FormItem>
    </RowElement>
  );
}
